/**
 * Decodes URL component.
 *
 * In contrast to standard [decodeURIComponent] function this one decodes `+` signs as spaces.
 *
 * [decodeURIComponent]: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/decodeURIComponent
 *
 * @param url - URL component to decode.
 *
 * @returns Decoded URL component.
 */
function decodeURLComponent(url) {
    return decodeURIComponent(url.replace(/\+/g, ' '));
}

/**
 * Escapes CSS identifier accordingly to the rules defined for [CSS.escape()](https://drafts.csswg.org/cssom/#the-css.escape%28%29-method)
 * utility method.
 *
 * Can be applied to CSS values as well, although it escapes characters that don't strictly need to be escaped.
 * A {@link escapeCSSVal} is a better alternative for that.
 *
 * @param text - A text to escape.
 *
 * @returns A string safe to be used as CSS identifier, e.g. as CSS selector.
 */
function escapeCSS(text) {
    const len = text.length;
    const first = text.charCodeAt(0);
    let out = '';
    let i = 0;
    if (first === 0x2d) {
        // If the first character is a "-" (U+002D)
        const second = text.charCodeAt(1);
        // ... and the second character is in the range [0-9] (U+0030 to U+0039).
        if (second > 0x2f && second < 0x3a) {
            // then '-' followed by the character escaped as code point.
            out += `-\\${second.toString(16)} `;
            i = 2;
        }
        else {
            out = '-';
            i = 1;
        }
        if (len === 1) {
            // ... and there is no second character, then the escaped character.
            return '\\-';
        }
    }
    else if (first > 0x2f && first < 0x3a) {
        // If the first character is in the range [0-9] (U+0030 to U+0039),
        // then the character escaped as code point.
        out += `\\${first.toString(16)} `;
        i = 1;
    }
    for (; i < len; ++i) {
        const c = text.charCodeAt(i);
        if (
        // Is in range [a-z] (U+0061 to U+007A),
        (c > 0x60 && c < 0x7b)
            // or is "-" (U+002D),
            || c === 0x2d
            // or is "_" (U+005F)
            || c === 0x5f
            // or is in range [0-9] (U+0030 to U+0039),
            || (c > 0x2f && c < 0x3a)
            // or is in range [A-Z] (U+0041 to U+005A)
            || (c > 0x40 && c < 0x5b)) {
            // then the character itself.
            out += text[i];
        }
        else if (c > 0x7e) {
            out += c === 0x7f
                // If the character is U+007F
                // then the character escaped as code point.
                ? `\\${c.toString(16)} `
                // If the character is greater than or equal to U+0080,
                // then the character itself
                : text[i];
        }
        else if (c < 0x20) {
            out += c
                // If the character is in the range [\1-\1f] (U+0001 to U+001F)
                // then the character escaped as code point.
                ? `\\${c.toString(16)} `
                // If the character is NULL (U+0000)
                // then the REPLACEMENT CHARACTER (U+FFFD).
                : '\uFFFD';
        }
        else {
            // Otherwise, the escaped character.
            out += `\\${text[i]}`;
        }
    }
    return out;
}

/**
 * Escapes CSS value to be included into CSS string.
 *
 * Escapes accordingly to [serialize a string] algorithm.
 *
 * [serialize a string]: https://drafts.csswg.org/cssom/#serialize-a-string
 *
 * @param text - A text to escape.
 *
 * @returns A string safe to be included into CSS value, e.g. within CSS string.
 */
function escapeCSSVal(text) {
    let out = '';
    const len = text.length;
    for (let i = 0; i < len; ++i) {
        const c = text.charCodeAt(i);
        out += c < 0x20 || c === 0x7f
            ? (c
                // If the character is in the range [\1-\1f] (U+0001 to U+001F),
                // the character escaped as code point.
                ? `\\${c.toString(16)} `
                // If the character is NULL (U+0000), then the REPLACEMENT CHARACTER (U+FFFD).;
                : '\uFFFD')
            : (c === 0x22 || c === 0x5c
                // If the character is '"' (U+0022) or "\" (U+005C),
                // the escaped character.
                ? `\\${text[i]}`
                // Otherwise, the character itself.
                : text[i]);
    }
    return out;
}

/**
 * @internal
 */
const xmlPredef = {
    '"': '&#34;',
    '&': '&amp;',
    '\'': '&#39;',
    '<': '&lt;',
    '>': '&gt;',
};
/**
 * @internal
 */
function replaceXMLPredef(char) {
    return xmlPredef[char];
}

/**
 * Replaces XML-unsafe characters with corresponding [predefined XML entities](https://en.wikipedia.org/wiki/List_of_XML_and_HTML_character_entity_references#Predefined_entities_in_XML).
 *
 * Uses the shortest possible entities representation. I.e. `&#34;` instead of `&quot;`, and `&#39;' instead of
 * `&apos;`.
 *
 * @param text - A text to escape.
 *
 * @returns XML-safe text.
 */
function escapeXML(text) {
    return text.replace(/[&<>'"]/g, replaceXMLPredef);
}

/**
 * @internal
 */
const uppercasePattern = /[A-Z]+/g;
/**
 * @internal
 */
function toHyphenLower(letters, offset, str) {
    const lowerCase = letters.toLowerCase();
    if ((lowerCase.length > 1) && (offset + lowerCase.length < str.length)) {
        // More than one subsequent upper-case letters, unless at the end of the string.
        return `-${lowerCase.slice(0, -1)}-${lowerCase.slice(-1)}`;
    }
    return '-' + lowerCase;
}
/**
 * @internal
 */
const hyphenateCSSName$cache = ( /*#__PURE__*/new Map());

/**
 * Hyphenates a camel-cased name.
 *
 * Applicable to CSS property names.
 *
 * Converts:
 *
 * - `backgroundColor` to `background-color`.
 *
 *    Uppercase letters converted to lower case and prepended by hyphen,
 *
 * - `MozTransition` to `-moz-transition`.
 *
 *   The very first uppercase letter is prepended by hyphen too.
 *
 * - `MSTransition` to `-ms-transition`.
 *
 *   Subsequent upper-case letters converted to lower case. The first and the last ones prepended by hyphen.
 *
 * - `someURL` to `some-url`
 *
 *   Subsequent upper-case letters in the very end of the string converted to lower case and prepended by hyphen.
 *
 * - `color` to `color`.
 *
 *   All lower-case names remain as is.
 *
 * - `padding-left` to `padding-left`
 *
 *   Hyphens remain in place.
 *
 * @param name - Camel-cased name to hyphenate.
 *
 * @returns Hyphenated name.
 */
function hyphenateName(name) {
    return name.replace(uppercasePattern, toHyphenLower);
}
/**
 * De-capitalizes a camel-cased name and hyphenates it.
 *
 * Calls {@link hyphenateName}, then removes the leading hyphen.
 *
 * Thus, e.g. `MozTransition` would be converted to `moz-transition` rather to `-moz-transition`.
 *
 * @param name - Camel-cased name to hyphenate.
 *
 * @returns Hyphenated name.
 */
function hyphenateDecapName(name) {
    const hyphenated = hyphenateName(name);
    return hyphenated.startsWith('-') ? hyphenated.substr(1) : hyphenated;
}
/**
 * Hyphenates a camel-cased CSS property key.
 *
 * Applicable to the names of `HTMLElement.style` properties, including vendor-specific ones.
 *
 * Calls {@link hyphenateName}, then replaces `ms-` prefix with `-ms-` one. Other vendor-specific prefixes are
 * capitalized, so the hyphen prefix is added already.
 *
 * Caches hyphenated names for the sake of speed.
 *
 * @param name - Camel-cased CSS property name to hyphenate.
 *
 * @returns Hyphenated CSS property name.
 */
function hyphenateCSSName(name) {
    const found = hyphenateCSSName$cache.get(name);
    if (found) {
        return found;
    }
    let hyphenated = hyphenateName(name);
    if (hyphenated.startsWith('ms-')) {
        hyphenated = '-' + hyphenated;
    }
    hyphenateCSSName$cache.set(name, hyphenated);
    return hyphenated;
}

export { decodeURLComponent, escapeCSS, escapeCSSVal, escapeXML as escapeHTML, escapeXML, hyphenateCSSName, hyphenateDecapName, hyphenateName };
//# sourceMappingURL=httongue.js.map
